/**
 * Which of a traveler's live trips to open from Home. Usually the newest one.
 * A trip with several cities has a live match per city (a host and a chat each):
 * open the city the trip is in today, or the next one still ahead, or the last
 * one once they are all over.
 */
export interface LiveMatch {
  id: string;
  status: string;
  created_at: string;
  /** The city of a trip with several cities (null for a trip booked as one block). */
  leg?: { start_date: string; days: number; position: number } | { start_date: string; days: number; position: number }[] | null;
}

function legOf(m: LiveMatch): { start_date: string; days: number; position: number } | null {
  return Array.isArray(m.leg) ? (m.leg[0] ?? null) : (m.leg ?? null);
}

function legEnd(leg: { start_date: string; days: number }): string {
  return new Date(Date.parse(`${leg.start_date}T00:00:00Z`) + (leg.days - 1) * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Where Home takes a traveler. `rows` newest first; `today` as YYYY-MM-DD.
 * The newest journey still a shortlist (no host chosen, not paid) comes first,
 * even when an older trip is live: otherwise its host choice and payment were
 * never shown. /matches sends on to the trip if that journey was paid meanwhile.
 */
export function travelerHomePath(rows: LiveMatch[], today: string): string {
  if (rows[0]?.status === 'proposed') return '/matches';
  const active = pickLiveMatch(rows, today);
  if (active) return `/trip/${active.id}/chat`;
  if (rows.some((m) => m.status === 'proposed')) return '/matches';
  const completed = rows.find((m) => m.status === 'completed');
  if (completed) return `/trip/${completed.id}/profile`;
  return '/try';
}

/** `rows` newest first; `today` as YYYY-MM-DD. */
export function pickLiveMatch(rows: LiveMatch[], today: string): LiveMatch | null {
  const live = rows.filter((m) => m.status === 'pending' || m.status === 'accepted');
  if (live.length === 0) return null;
  const cities = live.filter((m) => legOf(m)).sort((a, b) => legOf(a)!.position - legOf(b)!.position);
  if (cities.length > 1) {
    return cities.find((m) => legEnd(legOf(m)!) >= today) ?? cities[cities.length - 1];
  }
  return live[0];
}
